// #eslint-cleanup-web — shared error-message extractor for unknown
// errors thrown by axios + react-query + native Error. Replaces the
// repeated `(err: any) => err?.response?.data?.message ?? 'X'` pattern
// scattered across ~30 onError handlers in pages/components.
//
// Why a helper: the lint rule `@typescript-eslint/no-explicit-any`
// flags `(err: any)` correctly — server response shapes ARE unknown
// at compile time, so `unknown` is the right type. Inline narrowing
// at every call site adds 3-5 lines of cast-and-access boilerplate
// per handler; centralizing in one helper keeps diffs small + makes
// the canonical message-fallback chain easy to audit/change.
//
// Shape covered:
//   - Axios error:          err.response.data.message  OR err.response.data.error
//   - Native Error / thrown: err.message
//   - Plain string:          err
//   - Anything else:         fallback string
//
// Both `.message` and `.error` server keys are supported because
// existing handlers split unevenly across the two (admin endpoints
// return `error`; most others return `message`). The helper checks
// `message` first since it's the more common shape.

interface AxiosLikeError {
  response?: {
    data?: {
      message?: string
      error?: string
    }
  }
  message?: string
}

export function getErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (typeof err === 'string') return err
  if (err && typeof err === 'object') {
    const e = err as AxiosLikeError
    const fromData = e.response?.data?.message ?? e.response?.data?.error
    if (fromData) return fromData
    if (e.message) return e.message
  }
  return fallback
}
